import { Button } from 'primereact/button'
import ULModal, { ULModalProps } from '../../../../../shared/ul-modal/ULModal'
import { E2EProjectLineFlexTaskGroupItemModel } from '../../interfaces/pmr-e2e-project-task-group-item-model'
import usePromiseCallback from '../../../../../hooks/usePromiseCallback'
import ProjectService from '../../../../../services/ProjectService'
import { useEffect, useState } from 'react'
import PmrE2EProjectOrderlineModel from '../../interfaces/pmr-e2e-project-orderline-model'
import classes from './TaskChildrenCancelConfirmationDialog.module.scss'
import clsx from 'clsx'
import { ProgressSpinner } from 'primereact/progressspinner'
import ProjectTaskStatus from '../../enums/project-task-status'
import { E2EProjectLineMilestoneGroupItemModel } from '../../interfaces/pmr-e2e-project-milestone-group-item-model'
import { useSelector } from 'react-redux'
import { selectShowFlexTasks } from '../../../../../features/projectManagementPmr/projectManagementPmrSlice'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

export interface TaskChildrenCancelConfirmationDialogProps extends Omit<
  ULModalProps,
  'header' | 'footer'
> {
  taskChildren?: E2EProjectLineFlexTaskGroupItemModel[]
  milestoneChildren?: E2EProjectLineMilestoneGroupItemModel[]
  parentTaskId?: string
  taskId: string
  onCancel?: () => void
  onConfirm?: () => void
}

export default function TaskChildrenCancelConfirmationDialog({
  taskChildren,
  parentTaskId,
  taskId,
  milestoneChildren,
  onCancel,
  onConfirm,
  ...props
}: TaskChildrenCancelConfirmationDialogProps) {
  const showFlexTask = useSelector(selectShowFlexTasks)
  const taskMilestoneChildren = (showFlexTask.isOn ? taskChildren : milestoneChildren) ?? []
  const getTaskLines = usePromiseCallback(async () => {
    const request = {
      e2EProjectLineFlexTaskIds: !!parentTaskId
        ? [parentTaskId, taskId]
        : taskMilestoneChildren?.map(item => item.e2EProjectLineFlexTaskId),
    }

    const response = await ProjectService.getProjectOrderline(request)
    const data = (response.data as PmrE2EProjectOrderlineModel[]) ?? []

    const filteredData = data.filter(
      (orderline: PmrE2EProjectOrderlineModel) =>
        ![
          ProjectTaskStatus.Discontinued,
          ProjectTaskStatus.Cancelled,
          ProjectTaskStatus.Completed,
        ].includes(orderline.orderLineStatus)
    )
    return filteredData
  })

  const parentTask = getTaskLines.data?.find(item => item.e2EProjectLineFlexTaskId === parentTaskId)
  const isSiblingsCancelled = (parentTask?.taskChildren ?? [])
    .filter(child => child.e2EProjectLineFlexTaskId !== taskId)
    .every(child => child.flexTaskStatus === ProjectTaskStatus.Cancelled)
  const isSingleCancellation =
    (taskMilestoneChildren.length <= 0 && !parentTaskId) || !isSiblingsCancelled

  let data = getTaskLines.data ?? []

  if (parentTaskId) {
    data = data.filter(item => item.e2EProjectLineFlexTaskId === taskId)
  }

  const normalLineModalProps: Omit<ULModalProps, 'footer' | 'visible' | 'onHide'> = {
    header: showFlexTask.isOn ? 'Confirm Task Cancellation' : 'Confirm Milestone Cancellation',
  }

  const taskLineModalProps: Omit<ULModalProps, 'footer' | 'visible' | 'onHide'> = {
    style: {
      minWidth: '800px',
    },
    header: !getTaskLines.isLoading ? 'Parent Task Cancellation' : normalLineModalProps.header,
  }

  useEffect(() => {
    if (isSingleCancellation) return
    if (!props.visible) return
    getTaskLines.send()
  }, [isSingleCancellation, props.visible, taskChildren, milestoneChildren, parentTaskId])

  return (
    <ULModal
      {...props}
      {...(isSingleCancellation ? normalLineModalProps : taskLineModalProps)}
      theme="prime"
      contentClassName={classes['custom-modal']}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            className="default-cancel-button"
            onClick={() => {
              if (onCancel) onCancel()
              props.onHide()
            }}
            label={isSingleCancellation ? 'CANCEL' : 'NO'}
          />
          <Button
            className="default-save-button"
            onClick={() => {
              if (onConfirm) onConfirm()
            }}
            label={isSingleCancellation ? 'CONFIRM' : 'YES'}
          />
        </div>
      }
    >
      {getTaskLines.isLoading && (
        <div className={classes['loader']}>
          <div>
            <ProgressSpinner />
          </div>
          <span>Loading</span>
        </div>
      )}

      {(isSingleCancellation || data?.length === 0) && !getTaskLines.isLoading && (
        <>
          {showFlexTask.isOn && (
            <>
              Confirming will permanently remove this task from the order line's project lifecycle.
              <br /> <br />
              Proceed?
            </>
          )}
          {!showFlexTask.isOn && (
            <>
              Confirming will permanently remove this milestone from the order line's project
              lifecycle.
              <br /> <br />
              Proceed?
            </>
          )}
        </>
      )}

      {!isSingleCancellation && !getTaskLines.isLoading && data.length > 0 && (
        <>
          {!parentTask && (
            <p>Cancelling this task will mark the following child tasks as "Cancelled".</p>
          )}

          {!!parentTask && (
            <p>
              Cancelling this task will mark its parent task, '{[parentTask.flexTaskName]}', as
              "Cancelled"
            </p>
          )}

          <DataTable value={data} size="small" showGridlines={true} scrollable>
            <Column header="GMAWB Project" field="e2EProjectName" />
            <Column header="FLEX Project" field="flexProjectNumber" />
            <Column header="Order Line Number" field="orderLineDescription" />
            <Column header="FLEX Task(s)" field="flexTaskName" />
          </DataTable>

          <p>Do you want to proceed?</p>
        </>
      )}
    </ULModal>
  )
}
